import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pvrService } from '../services/pvrService';
import type { PVRRow, PVRDocType, PVRDocTypeSummary } from '../types';
import { DataLoadingStatus } from 'shared/types/common';

export function usePVR() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialCatNum = searchParams.get('cat_num') ?? '';

  const [catNum, setCatNum] = useState<string | number>(initialCatNum);
  const [status, setStatus] = useState<DataLoadingStatus>(
    initialCatNum ? DataLoadingStatus.Loading : DataLoadingStatus.NotLoaded
  );
  const [rows, setRows] = useState<PVRRow[] | undefined>(undefined);
  const [selectedDocTypeIdx, setSelectedDocTypeIdx] = useState(0);

  const availableDocTypes = useMemo<PVRDocTypeSummary[]>(() => {
    if (!rows || rows.length === 0) return [];

    const counts: Partial<Record<PVRDocType, number>> = {};
    for (const row of rows) {
      counts[row.docType] = (counts[row.docType] ?? 0) + 1;
    }

    return Object.entries(counts) as PVRDocTypeSummary[];
  }, [rows]);

  const load = useCallback(
    async (numToLoad: string | number = catNum) => {
      setStatus(DataLoadingStatus.Loading);
      try {
        const resp = await pvrService.getPVR(numToLoad as number);
        if (resp.success && resp.result) {
          setRows(resp.result);
          setSelectedDocTypeIdx(0);
          setStatus(DataLoadingStatus.Loaded);
        } else {
          setRows(undefined);
          setStatus(DataLoadingStatus.Error);
        }
      } catch {
        setRows(undefined);
        setStatus(DataLoadingStatus.Error);
      }
    },
    [catNum]
  );

  const submit = useCallback(() => {
    navigate(`/pvr?cat_num=${catNum}`);
    load(catNum);
  }, [catNum, navigate, load]);

  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  if (initialCatNum && !hasAutoLoaded) {
    setHasAutoLoaded(true);
    load(initialCatNum);
  }

  return {
    catNum,
    setCatNum,

    status,
    rows,

    availableDocTypes,
    selectedDocTypeIdx,
    setSelectedDocTypeIdx,

    submit,
  };
}